import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import KeyboardShortcuts from "@/components/KeyboardShortcuts";
import { Loader2 } from "lucide-react";
import { ADVANCED_FEATURES_ENABLED } from "@/lib/features";

// Eager load critical pages — dashboard is the primary destination after login,
// lazy loading it adds an unnecessary Suspense spinner during the login→dashboard transition.
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import Layout from "@/components/Layout";
import DebugPage from "@/pages/DebugPage";

// Lazy load non-critical pages
const ExamSimulationPage = lazy(() => import("@/pages/ExamSimulationPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
// AuthCallback removed - Google Auth disabled
const SpecialtyPage = lazy(() => import("@/pages/SpecialtyPage"));
const QuizPage = lazy(() => import("@/pages/QuizPage"));
const FavoritesPage = lazy(() => import("@/pages/FavoritesPage"));
const ReviewPage = lazy(() => import("@/pages/ReviewPage"));
const SearchResultsPage = lazy(() => import("@/pages/SearchResultsPage"));
const StatsPage = lazy(() => import("@/pages/StatsPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AdminAnalyticsPage = lazy(() => import("@/pages/AdminAnalyticsPage"));
const AdminQuestionImportPage = lazy(() => import("@/pages/AdminQuestionImportPage"));
const LeaderboardPage = lazy(() => import("@/pages/LeaderboardPage"));
const NotebookPage = lazy(() => import("@/pages/NotebookPage"));
const CustomQuizPage = lazy(() => import("@/pages/CustomQuizPage"));
const AnalyzerPage = lazy(() => import("@/pages/AnalyzerPage"));
const MyNotesPage = lazy(() => import("@/pages/MyNotesPage"));
const BillingPage = lazy(() => import("@/pages/BillingPage"));
const BillingSuccessPage = lazy(() => import("@/pages/BillingSuccessPage"));
const DailyPodcastPage = lazy(() => import("@/pages/DailyPodcastPage"));
const LerntoolsPage = lazy(() => import("@/pages/LerntoolsPage"));
const RagPage = lazy(() => import("@/pages/RagPage"));
const DicomPage = lazy(() => import("@/pages/DicomPage"));
const GuestQuizPage = lazy(() => import("@/pages/GuestQuizPage"));
const ChallengePage = lazy(() => import("@/pages/ChallengePage"));
const SEOSpecialtyPage = lazy(() => import("@/pages/SEOSpecialtyPage"));
const SpacedReviewPage = lazy(() => import("@/pages/SpacedReviewPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const ImpressumPage = lazy(() => import("@/pages/ImpressumPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const MessagingPage = lazy(() => import("@/pages/MessagingPage"));
const CommunityPage = lazy(() => import("@/pages/CommunityPage"));
const CommunityPostPage = lazy(() => import("@/pages/CommunityPostPage"));
const ModerationQueuePage = lazy(() => import("@/pages/ModerationQueuePage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const KpReportsPage = lazy(() => import("@/pages/KpReportsPage"));
const KpReportDetailPage = lazy(() => import("@/pages/KpReportDetailPage"));
const GermanyHomePage = lazy(() => import("@/pages/Germany/GermanyHomePage"));
const SwitzerlandHomePage = lazy(() => import("@/pages/Switzerland/SwitzerlandHomePage"));
const FSPSimulationPage = lazy(() => import("@/pages/Germany/FSPSimulationPage"));
const KPSimulationPage = lazy(() => import("@/pages/Germany/KPSimulationPage"));
const ArztbriefPage = lazy(() => import("@/pages/Germany/ArztbriefPage"));
const MasterclassPage = lazy(() => import("@/pages/Germany/MasterclassPage"));
const WeaknessPage = lazy(() => import("@/pages/WeaknessPage"));
const KnowledgeLabPage = lazy(() => import("@/pages/KnowledgeLabPage"));

// API base URL for legacy raw axios calls in pages
import { API } from "@/lib/api";
export { API };

// Auth Context
export const AuthContext = createContext(null);

// Theme Context
export const ThemeContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === "light" ? "dark" : "light");

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null);

  // Keep ref in sync so checkAuth can read latest user without depending on object reference
  userRef.current = user;

  const checkAuth = useCallback(async () => {
    if (token) {
      if (userRef.current) {
        setLoading(false);
        return;
      }
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        setUser(response.data);
      } catch (error) {
        console.error("Token verification failed:", error);
        localStorage.removeItem("token");
        setToken(null);
      } finally {
        clearTimeout(timeoutId);
      }
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);


  // Re-verify auth when tab becomes visible (handles stale tokens after sleep)
  useEffect(() => {
    if (!token) return;
    const handleVisibility = () => {
      if (!document.hidden) checkAuth();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [token, checkAuth]);

  // Sync logout across tabs via storage events
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "token" && !e.newValue) {
        setUser(null);
        setToken(null);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Graceful logout when apiClient intercepts a 401 (token expired/revoked)
  useEffect(() => {
    const handle401 = () => { setUser(null); setToken(null); };
    window.addEventListener("auth:logout", handle401);
    return () => window.removeEventListener("auth:logout", handle401);
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const data = response.data;
    if (data.token) {
      localStorage.setItem("token", data.token);
      setToken(data.token);
      setUser(data.user);
    }
    return data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const ctxValue = useMemo(() => ({ user, token, loading, login, register, logout }), [user, token, loading]);

  return (
    <AuthContext.Provider value={ctxValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

// App Router with OAuth callback detection
function AppRouter() {
  
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/__debug" element={<DebugPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/guest-quiz" element={<GuestQuizPage />} />
          <Route path="/fragen/:specialtyId" element={<SEOSpecialtyPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPage />} />
          <Route path="/challenge/:challengeId" element={
            <ProtectedRoute><ChallengePage /></ProtectedRoute>
          } />
          <Route path="/spaced-review" element={
            <ProtectedRoute><SpacedReviewPage /></ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute><DashboardPage /></ProtectedRoute>
          } />
          <Route path="/exam-simulation" element={
            <ProtectedRoute><ExamSimulationPage /></ProtectedRoute>
          } />
          <Route path="/specialty/:specialtyId" element={
            <ProtectedRoute><SpecialtyPage /></ProtectedRoute>
          } />
          <Route path="/quiz/:specialtyId" element={
            <ProtectedRoute><QuizPage /></ProtectedRoute>
          } />
          <Route path="/quiz" element={
            <ProtectedRoute><LegacyQuizRedirect /></ProtectedRoute>
          } />
          <Route path="/favorites" element={
            <ProtectedRoute><FavoritesPage /></ProtectedRoute>
          } />
          <Route path="/review" element={
            <ProtectedRoute><ReviewPage /></ProtectedRoute>
          } />
          <Route path="/weaknesses" element={
            <ProtectedRoute><WeaknessPage /></ProtectedRoute>
          } />
          <Route path="/search" element={
            <ProtectedRoute><SearchResultsPage /></ProtectedRoute>
          } />
          <Route path="/stats" element={
            <ProtectedRoute><StatsPage /></ProtectedRoute>
          } />
          <Route path="/leaderboard" element={
            <ProtectedRoute><LeaderboardPage /></ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>
          } />
          <Route path="/admin/analytics" element={
            <ProtectedRoute adminOnly><AdminAnalyticsPage /></ProtectedRoute>
          } />
          <Route path="/admin/moderation" element={
            <ProtectedRoute adminOnly><ModerationQueuePage /></ProtectedRoute>
          } />
          <Route path="/admin/question-import" element={
            <ProtectedRoute adminOnly><AdminQuestionImportPage /></ProtectedRoute>
          } />
          <Route path="/knowledge-lab" element={
            <ProtectedRoute adminOnly><KnowledgeLabPage /></ProtectedRoute>
          } />
          <Route path="/notebook" element={
            <ProtectedRoute><NotebookPage /></ProtectedRoute>
          } />
          <Route path="/custom-quiz" element={
            <ProtectedRoute><CustomQuizPage /></ProtectedRoute>
          } />
          <Route path="/analyzer" element={
            <ProtectedRoute><AnalyzerPage /></ProtectedRoute>
          } />
          {ADVANCED_FEATURES_ENABLED && (
            <>
              <Route path="/rag" element={
                <ProtectedRoute><RagPage /></ProtectedRoute>
              } />
              <Route path="/dicom" element={
                <ProtectedRoute><DicomPage /></ProtectedRoute>
              } />
            </>
          )}
          <Route path="/my-notes" element={
            <ProtectedRoute><MyNotesPage /></ProtectedRoute>
          } />
          <Route path="/billing" element={
            <ProtectedRoute><BillingPage /></ProtectedRoute>
          } />
          <Route path="/billing/success" element={
            <ProtectedRoute><BillingSuccessPage /></ProtectedRoute>
          } />
          <Route path="/lerntools" element={
            <ProtectedRoute><LerntoolsPage /></ProtectedRoute>
          } />
          <Route path="/messages" element={
            <ProtectedRoute><MessagingPage /></ProtectedRoute>
          } />
          <Route path="/notifications" element={
            <ProtectedRoute><NotificationsPage /></ProtectedRoute>
          } />
          <Route path="/community" element={
            <ProtectedRoute><CommunityPage /></ProtectedRoute>
          } />
          <Route path="/community/:postId" element={
            <ProtectedRoute><CommunityPostPage /></ProtectedRoute>
          } />
          <Route path="/de" element={<GermanyHomePage />} />
          <Route path="/ch" element={<SwitzerlandHomePage />} />
          <Route path="/de/fsp" element={
            <ProtectedRoute><FSPSimulationPage /></ProtectedRoute>
          } />
          <Route path="/de/kp-simulation" element={
            <ProtectedRoute><KPSimulationPage /></ProtectedRoute>
          } />
          <Route path="/de/arztbrief" element={
            <ProtectedRoute><ArztbriefPage /></ProtectedRoute>
          } />
          <Route path="/de/masterclass" element={
            <ProtectedRoute><MasterclassPage /></ProtectedRoute>
          } />
          <Route path="/kp-reports" element={<KpReportsPage />} />
          <Route path="/kp-reports/:reportId" element={<KpReportDetailPage />} />
          <Route path="/podcast" element={<DailyPodcastPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<PrivacyPage />} />
          <Route path="/agb" element={<TermsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function LegacyQuizRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  if (params.get("country") === "switzerland") {
    if (!params.get("limit")) params.set("limit", "50");
  }

  const search = params.toString();
  return <Navigate to={`/quiz/custom${search ? `?${search}` : ""}`} replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <KeyboardShortcuts />
            <Toaster position="top-center" richColors closeButton dir="ltr" />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
